import React from "react";
import {
  Badge,
  User,
  putCommasIntoNumber,
  sparklyLeaderboardIds,
  memberLeaderboardIds,
  businessLeaderboardIds,
  LeaderboardStats,
  BadgeCategory,
} from "@ecologi/shared";

import styles from "../styles/AchievementProgressBar.module.scss";

interface Props {
  badge: Badge;
  user: User;
  leaderboardStats: LeaderboardStats | null;
}

export const AchievementProgressBar: React.FC<Props> = ({
  badge,
  user,
  leaderboardStats,
}) => {
  const { width, stat } = calculateWidth({ badge, user, leaderboardStats });

  return (
    <>
      <div className={styles.progressBar}>
        <div
          className={styles.progressBarInner}
          data-width={width}
          style={{ width }}
        />
      </div>
      <p className={styles.progressBarStat}>{stat}</p>
    </>
  );
};

interface Args {
  badge: Badge;
  user: User;
  leaderboardStats: any;
}

const calculateWidth = ({ badge, user, leaderboardStats }: Args) => {
  let width = 0;
  let stat = "";

  if (badge.category === BadgeCategory.TreesPlanted) {
    const numTrees = user.totalNumberOfTrees();
    width = Math.floor((numTrees / badge.total) * 100);
    stat = `${putCommasIntoNumber(numTrees)} / ${putCommasIntoNumber(
      badge.total
    )} trees`;
  }

  if (badge.category === BadgeCategory.SparklyTrees) {
    const numTrees = user.totalNumberOfSparkleTrees();
    width = Math.floor((numTrees / badge.total) * 100);
    stat = `${putCommasIntoNumber(numTrees)} / ${putCommasIntoNumber(
      badge.total
    )} sparkly trees`;
  }

  if (badge.category === BadgeCategory.ClimatePositive) {
    const numMonths = user.subscription?.numberOfMonthsPaid || 0;
    width = Math.floor((numMonths / badge.total) * 100);
    stat = `${numMonths} / ${badge.total} months`;
  }

  if (badge.category === BadgeCategory.CarbonReduction) {
    const numTonnes = user.totalNumberOfTonnesOffset();
    width = Math.floor((numTonnes / badge.total) * 100);
    stat = `${numTonnes} / ${badge.total} tonnes`;
  }

  if (badge.category === BadgeCategory.Leaderboard) {
    const sparklyIdIndex = sparklyLeaderboardIds.findIndex(
      (id) => id === badge.id
    );
    const memberIdIndex = memberLeaderboardIds.findIndex(
      (id) => id === badge.id
    );
    const businessIdIndex = businessLeaderboardIds.findIndex(
      (id) => id === badge.id
    );

    const { key, index } =
      sparklyIdIndex >= 0
        ? { key: "sparkly", index: sparklyIdIndex }
        : memberIdIndex >= 0
        ? { key: "members", index: memberIdIndex }
        : businessIdIndex >= 0
        ? { key: "business", index: businessIdIndex }
        : { key: "", index: -1 };

    if (key && leaderboardStats) {
      let leaderboardStatsForBadge = leaderboardStats[key];
      // if it's top 10 or #1, slice the data
      if (index === 0) {
        leaderboardStatsForBadge = leaderboardStatsForBadge.slice(0, 1);
      }
      if (index === 1) {
        leaderboardStatsForBadge = leaderboardStatsForBadge.slice(0, 10);
      }
      if (index === 2) {
        leaderboardStatsForBadge = leaderboardStatsForBadge.slice(0, 50);
      }
      if (index === 3) {
        leaderboardStatsForBadge = leaderboardStatsForBadge.slice(0, 100);
      }

      const leaderboardUser = leaderboardStatsForBadge.pop();

      const numTrees =
        key === "sparkly"
          ? user.totalNumberOfSparkleTrees()
          : user.totalNumberOfTrees();
      width =
        numTrees > 0
          ? Math.floor((numTrees / (leaderboardUser.trees + 1)) * 100)
          : 0;
      const numTreesAway = leaderboardUser.trees + 1 - numTrees;
      const numTreesAwayStr = putCommasIntoNumber(numTreesAway);
      stat =
        index > 0
          ? `${numTreesAwayStr} ${key === "sparkly" ? "sparkly " : ""}tree${
              numTreesAway === 1 ? "" : "s"
            } away from the top ${index === 1 ? 10 : index === 2 ? 50 : 100}`
          : `${numTreesAwayStr} ${key === "sparkly" ? "sparkly " : ""}tree${
              numTreesAway === 1 ? "" : "s"
            } away from the top spot`;
    }
  }

  width = width > 100 ? 100 : width;

  return { width: width + "%", stat };
};
